import { useCallback, useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

interface UseScrollSyncProps {
  allColumnIds: string[];
  activeSection: string | null;
  setActiveSection: (section: string | null) => void;
}

export function useScrollSync({
  allColumnIds,
  activeSection,
  setActiveSection,
}: UseScrollSyncProps) {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToItem = useCallback(
    (section: string) => {
      const index = allColumnIds.indexOf(section);
      itemRefs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    },
    [allColumnIds]
  );

  const handleScroll = useCallback(() => {
    let maxVisibility = 0;
    let currentActiveSection = activeSection;

    itemRefs.current.forEach((section, index) => {
      if (section) {
        const rect = section.getBoundingClientRect();
        const containerRectLeft =
          scrollContainerRef.current?.getBoundingClientRect().left ?? 0;
        const visibleWidth =
          Math.min(
            rect.right - containerRectLeft,
            scrollContainerRef.current?.clientWidth || 0
          ) - Math.max(rect.left - containerRectLeft, 0);

        if (visibleWidth > maxVisibility + 10) {
          maxVisibility = visibleWidth;
          currentActiveSection = allColumnIds[index];
        }
      }
    });

    if (currentActiveSection !== activeSection) {
      setActiveSection(currentActiveSection);
    }
  }, [activeSection, allColumnIds, setActiveSection]);

  const handleScrollDebounced = useDebouncedCallback(handleScroll, 100);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScrollDebounced);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScrollDebounced);
      }
    };
  }, [handleScrollDebounced]);

  return {
    itemRefs,
    scrollContainerRef,
    scrollToItem,
  };
}
